import {
    getAssignBoundaryToApplicationUrl,
    getGetApplicationUrl,
    getUpdateApplicationUrl
} from 'generatedTypes/flight/services/application-controller';
import { useState } from 'react';

import useFetch from 'hooks/useFetch';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { APPLICATIONS_API } from './constants';
import type {
    ApplicationResponse,
    ApplicationUpdateParams,
    AssignBoundaryToApplication,
    AssignBoundaryToApplicationParams,
    CreateApplication,
    FetchApplication,
    UpdateApplication
} from './types';

const useApplication = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet, authenticatedPost, authenticatedPut } = useFetch();
    const { withRetry, withRetryAndDuplicateCheck } = useRetry();
    const { APPLICATION_NAME_DUPLICATE_ERROR, CONNECTION_ERROR, SAVE_FAILED } = useTranslation();

    const createApplication: CreateApplication = async ({ application }) => {
        try {
            setIsLoading(true);

            return await withRetryAndDuplicateCheck(
                () =>
                    authenticatedPost<ApplicationResponse>(
                        APPLICATIONS_API,
                        JSON.stringify(application)
                    ),
                SAVE_FAILED,
                APPLICATION_NAME_DUPLICATE_ERROR,
                () => createApplication({ application })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const fetchApplication: FetchApplication = async ({ id }) => {
        try {
            setIsLoading(true);
            const url = getGetApplicationUrl(id);

            return await withRetry(
                () => authenticatedGet<ApplicationResponse>(url),
                CONNECTION_ERROR,
                () => fetchApplication({ id })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const updateApplication: UpdateApplication = async ({
        application,
        applicationId
    }: ApplicationUpdateParams) => {
        try {
            setIsLoading(true);
            const url = getUpdateApplicationUrl(applicationId);

            return await withRetry(
                () => authenticatedPut<ApplicationResponse>(url, JSON.stringify(application)),
                SAVE_FAILED,
                () => updateApplication({ application, applicationId })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const assignBoundaryToApplication: AssignBoundaryToApplication = async ({
        applicationId,
        boundaryId
    }: AssignBoundaryToApplicationParams) => {
        try {
            setIsLoading(true);
            const url = getAssignBoundaryToApplicationUrl(applicationId, boundaryId);

            return await withRetry(
                () => authenticatedPut(url, null),
                SAVE_FAILED,
                () => assignBoundaryToApplication({ applicationId, boundaryId })
            );
        } finally {
            setIsLoading(false);
        }
    };

    return {
        assignBoundaryToApplication,
        createApplication,
        fetchApplication,
        isLoading,
        updateApplication
    };
};

export default useApplication;
